import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

function Plans() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [activeTab, setActiveTab] = useState('packages');
  const [processingPlan, setProcessingPlan] = useState(null);
  const [processingPack, setProcessingPack] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [plansRes, packsRes, subRes] = await Promise.all([
          axios.get(`${API}/subscription/plans`),
          axios.get(`${API}/subscription/packs`),
          axios.get(`${API}/subscription`)
        ]);
        setPlans(plansRes.data.plans || []);
        setPacks(packsRes.data.packs || []);
        setSubscription(subRes.data);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load plans");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSelectPlan = async (planId) => {
    if (planId === 'free') {
      toast.info("You're already on the Free plan or downgrading requires cancellation");
      return;
    }

    if (planId === subscription?.plan) {
      toast.info("You're already on this plan");
      return;
    }

    setProcessingPlan(planId);
    try {
      const response = await axios.post(`${API}/billing/checkout/subscription`, {
        plan: planId,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleSelectPack = async (packId) => {
    setProcessingPack(packId);
    try {
      const response = await axios.post(`${API}/billing/checkout/pack`, {
        pack: packId,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setProcessingPack(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPlanHighlight = (planId) => {
    if (planId === 'basic') return { border: '2px solid #2563eb', shadow: '0 0 0 4px rgba(37, 99, 235, 0.1)' };
    return {};
  };

  if (loading) {
    return (
      <div className="loading" data-testid="plans-loading">
        <div className="spinner"></div>
        <p>Loading plans...</p>
      </div>
    );
  }

  return (
    <div data-testid="plans-page">
      <div className="page-header">
        <h1 className="page-title">Plans & Pricing</h1>
        <p className="page-description">
          Choose the plan that fits your lead generation needs
        </p>
      </div>

      {/* Current Plan Banner */}
      {subscription && subscription.plan !== 'free' && (
        <div style={{
          background: 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
          border: '1px solid #86efac',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <span style={{ fontSize: '14px', color: '#166534' }}>Current Plan: </span>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#166534' }}>{subscription.plan_name}</span>
          </div>
          <Button
            onClick={() => navigate('/billing')}
            className="btn"
            style={{ background: '#16a34a', color: 'white' }}
          >
            Manage Billing
          </Button>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '4px',
        background: '#f1f5f9',
        padding: '4px',
        borderRadius: '12px',
        marginBottom: '32px',
        width: 'fit-content'
      }}>
        <button
          onClick={() => setActiveTab('packages')}
          data-testid="tab-packages"
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'packages' ? 'white' : 'transparent',
            color: activeTab === 'packages' ? '#1e293b' : '#64748b',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: activeTab === 'packages' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          Monthly Packages
        </button>
        <button
          onClick={() => setActiveTab('packs')}
          data-testid="tab-packs"
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'packs' ? 'white' : 'transparent',
            color: activeTab === 'packs' ? '#1e293b' : '#64748b',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: activeTab === 'packs' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          Lead Packs (Add-ons)
        </button>
      </div>

      {/* Monthly Packages Tab */}
      {activeTab === 'packages' && (
        <div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {plans.map((plan) => {
              const isCurrentPlan = subscription?.plan === plan.id;
              const highlight = getPlanHighlight(plan.id);
              const isPopular = plan.id === 'basic';

              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: highlight.border || '1px solid #e2e8f0',
                    boxShadow: highlight.shadow || '0 4px 6px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = highlight.shadow || '0 4px 6px rgba(0,0,0,0.05)';
                  }}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: '#2563eb',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase'
                    }}>
                      Most Popular
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {isCurrentPlan && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      background: '#16a34a',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}>
                      Current Plan
                    </div>
                  )}

                  <div style={{ padding: '32px 24px' }}>
                    {/* Plan Name */}
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '8px',
                      marginTop: isPopular || isCurrentPlan ? '16px' : '0'
                    }}>
                      {plan.name}
                    </h3>

                    {/* Price */}
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ fontSize: '40px', fontWeight: '800', color: '#1e293b' }}>
                        {plan.price === 0 ? '$0' : formatCurrency(plan.price)}
                      </span>
                      {plan.price > 0 && (
                        <span style={{ fontSize: '16px', color: '#64748b' }}>/month</span>
                      )}
                    </div>

                    {/* Features */}
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                      {plan.leads_per_month > 0 && plan.id !== 'free' && (
                        <li style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                          <span style={{ color: '#16a34a', fontSize: '18px' }}>&#10003;</span>
                          <span style={{ fontSize: '14px', color: '#334155' }}>
                            <strong>{plan.leads_per_month}</strong> verified leads/month
                          </span>
                        </li>
                      )}
                      {(plan.features || []).map((feat) => (
                        <li key={feat.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                          <span style={{ color: feat.included ? '#16a34a' : '#dc2626', fontSize: '18px' }}>{feat.included ? '\u2713' : '\u2717'}</span>
                          <span style={{ fontSize: '14px', color: feat.included ? '#334155' : '#94a3b8', textDecoration: feat.included ? 'none' : 'line-through' }}>
                            {feat.label}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    {plan.id === 'free' ? (
                      <button
                        disabled
                        style={{
                          width: '100%',
                          padding: '14px',
                          borderRadius: '10px',
                          border: '1px solid #e2e8f0',
                          background: '#f8fafc',
                          color: '#94a3b8',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'not-allowed'
                        }}
                      >
                        Free Plan
                      </button>
                    ) : isCurrentPlan ? (
                      <button
                        disabled
                        style={{
                          width: '100%',
                          padding: '14px',
                          borderRadius: '10px',
                          border: '1px solid #16a34a',
                          background: '#f0fdf4',
                          color: '#16a34a',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'not-allowed'
                        }}
                      >
                        &#10003; Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={processingPlan === plan.id}
                        data-testid={`select-plan-${plan.id}`}
                        style={{
                          width: '100%',
                          padding: '14px',
                          borderRadius: '10px',
                          border: 'none',
                          background: isPopular ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : '#1e293b',
                          color: 'white',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: processingPlan === plan.id ? 'wait' : 'pointer',
                          opacity: processingPlan === plan.id ? 0.7 : 1,
                          transition: 'opacity 0.2s ease'
                        }}
                      >
                        {processingPlan === plan.id ? 'Processing...' : 'Select Plan'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info Box */}
          <div style={{
            marginTop: '32px',
            padding: '20px',
            background: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
              How it works
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#64748b', fontSize: '14px', lineHeight: '1.8' }}>
              <li>Only verified emails count against your monthly limit</li>
              <li>Need teammates on Basic or Starter? Add the Team add-on (3 members) for $50/month from the Team page</li>
              <li>Billing renews every 30 days from your subscription date</li>
              <li>Cancel anytime - you'll keep access until the end of your billing period</li>
              <li>Upgrade or purchase add-on packs if you need more leads</li>
            </ul>
          </div>
        </div>
      )}

      {/* Lead Packs Tab */}
      {activeTab === 'packs' && (
        <div>
          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px'
          }}>
            <p style={{ fontSize: '14px', color: '#1e40af', margin: 0 }}>
              <strong>Lead Packs</strong> are one-time purchases that add bonus leads to your account. 
              These leads never expire and stack on top of your monthly allowance.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {packs.map((pack) => (
              <div
                key={pack.id}
                data-testid={`pack-card-${pack.id}`}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  padding: '32px 24px',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
                }}
              >
                {/* Pack Badge */}
                <div style={{
                  display: 'inline-block',
                  background: '#f0fdf4',
                  color: '#16a34a',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '700',
                  marginBottom: '16px'
                }}>
                  +{pack.leads} Leads
                </div>

                {/* Pack Name */}
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: '8px'
                }}>
                  {pack.name}
                </h3>

                {/* Price */}
                <div style={{ marginBottom: '24px' }}>
                  <span style={{ fontSize: '36px', fontWeight: '800', color: '#1e293b' }}>
                    {formatCurrency(pack.price)}
                  </span>
                  <span style={{ fontSize: '14px', color: '#64748b', marginLeft: '8px' }}>
                    one-time
                  </span>
                </div>

                {/* Price per lead */}
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
                  {formatCurrency(pack.price / pack.leads)} per lead
                </p>

                {/* Features */}
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ color: '#16a34a', fontSize: '16px' }}>&#10003;</span>
                    <span style={{ fontSize: '14px', color: '#334155' }}>
                      Added to your account instantly
                    </span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ color: '#16a34a', fontSize: '16px' }}>&#10003;</span>
                    <span style={{ fontSize: '14px', color: '#334155' }}>
                      Never expires
                    </span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#16a34a', fontSize: '16px' }}>&#10003;</span>
                    <span style={{ fontSize: '14px', color: '#334155' }}>
                      Stacks with monthly limit
                    </span>
                  </li>
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPack(pack.id)}
                  disabled={processingPack === pack.id}
                  data-testid={`buy-pack-${pack.id}`}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: processingPack === pack.id ? 'wait' : 'pointer',
                    opacity: processingPack === pack.id ? 0.7 : 1,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  {processingPack === pack.id ? 'Processing...' : 'Buy Now'}
                </button>
              </div>
            ))}
          </div>

          {/* Current Bonus Leads */}
          {subscription?.bonus_leads > 0 && (
            <div style={{
              marginTop: '32px',
              padding: '20px',
              background: '#f0fdf4',
              borderRadius: '12px',
              border: '1px solid #bbf7d0'
            }}>
              <p style={{ fontSize: '15px', color: '#166534', margin: 0 }}>
                <strong>You have {subscription.bonus_leads} bonus leads</strong> available in your account
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Plans;
